import { Modal, Box } from "@mui/material";

import clsx from "clsx";
import { Wrapper } from "./styles";
import { useCallback, useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import { FaRegEdit, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import { ReactComponent as SettingsSvg } from "../../../assets/svgs/settings.svg";
import { Button } from "../../../components/Button";
import { ConfigureTableColumnsMenu } from "../../../components/ConfigureTableColumnsMenu";
import { ColumnFilterValuesMenu } from "../../../components/Table/Filters/ColumnFilterValuesMenu";
import { Table as DataTable, type Column } from "../../../components/Table";
import CustomButton from "./CustomButton";
import { downloadInteractionsExcel } from "./exportInteractionsExcel";
import NewMeeting from "./NewMeeting";
import type { InteractionProps } from "./interfaces";
import {
  INTERACTIONS_TABLE_COLUMNS_STORAGE_KEY,
  MEETING_TABLE_COLUMN_SPECS,
  renderMeetingDataCell,
} from "./interactionsTableCells";
import { api } from "../../../services/api";
import {
  fetchInteractionColumnValues,
  fetchInteractionsForExport,
  useInteractionsQuery,
} from "../../../services/hooks/interactions/queries/useInteractionsQuery";
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from "../../../constants/toast";
import { useAuth } from "../../../contexts/AuthContext";
import { UserType } from "../../../enumns/userType";
import type { InteractionsFiltersDomain } from "../../../types/domain/interactions-filters-domain";
import { getErrorMessage } from "../../../utils/getErrorMessage";

function readStoredColumnVisibility(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(INTERACTIONS_TABLE_COLUMNS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
  } catch {
    /* ignore */
  }
  return {};
}

const Interactions: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.type === UserType.ADMIN;
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<InteractionsFiltersDomain>({
    page: 1,
    take: 20,
    includeOlder: false,
    order: "DESC",
    columnFilters: {},
  });

  const { data, isLoading } = useInteractionsQuery(filters);
  const interactions = data?.data ?? [];
  const hasOlderRecords = data?.meta?.hasOlderRecords ?? false;

  const [selectedDoc, setSelectedDoc] = useState<InteractionProps | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isTableColumnsConfigMenuOpen, setIsTableColumnsConfigMenuOpen] =
    useState(false);
  const [columnsVisibility, setColumnsVisibility] = useState<
    Record<string, boolean>
  >(() => readStoredColumnVisibility());
  const [patchingFieldKey, setPatchingFieldKey] = useState<string | null>(null);

  const invalidateInteractions = useCallback(async () => {
    await queryClient.invalidateQueries(["interactions"]);
  }, [queryClient]);

  const newEntry = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#fff",
    borderRadius: "12px",
    boxShadow: 24,
    p: 2,
    width: "min(96vw, 1280px)",
    maxWidth: "96vw",
    height: "auto",
    maxHeight: "calc(100dvh - 24px)",
    overflowY: "visible",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const handleCloseModal = () => {
    setUploadModalOpen(false);
    setSelectedDoc(null);
    setEdit(false);
  };

  const handleInteractionSaved = useCallback(async () => {
    await invalidateInteractions();
    setUploadModalOpen(false);
    setSelectedDoc(null);
    setEdit(false);
  }, [invalidateInteractions]);

  const ACTIONS_COLUMN_WIDTH_PX = 62;

  const getColumnMinWidth = useCallback((field: string) => {
    if (field === "id") return 56;
    if (field === "ra") return 72;
    if (field === "cliente") return 120;
    if (field === "projeto") return 84;
    if (field === "data") return 78;
    if (field === "paymentDay") return 112;
    if (field === "feeCredito") return 104;
    if (field === "hora") return 58;
    if (field === "nome") return 88;
    if (field === "duracao" || field === "duracaoReal") return 120;
    if (field === "valorSerPago") return 112;
    if (field === "proativo" || field === "tipo") return 110;
    if (field === "agencia" || field === "conta") return 120;
    return 180;
  }, []);

  const columnFitsContent = useCallback((field: string) => {
    return (
      field === "data" ||
      field === "hora" ||
      field === "nome" ||
      field === "paymentDay"
    );
  }, []);

  const columnSpecsForRole = MEETING_TABLE_COLUMN_SPECS;

  const columnsForConfigureMenu = useMemo<Column[]>(
    () =>
      columnSpecsForRole.map(({ field, headerName }) => ({
        field,
        headerName,
        isVisible:
          columnsVisibility[field] !== undefined
            ? columnsVisibility[field]
            : true,
      })),
    [columnSpecsForRole, columnsVisibility]
  );

  const visibleColumnSpecs = useMemo(
    () =>
      columnSpecsForRole.filter((spec) =>
        columnsVisibility[spec.field] !== undefined
          ? columnsVisibility[spec.field]
          : true
      ),
    [columnSpecsForRole, columnsVisibility]
  );

  const stickyLeftByField = useMemo(() => {
    const stickyMap: Record<string, number> = {};
    const lastStickyIdx = MEETING_TABLE_COLUMN_SPECS.findIndex(
      (s) => s.field === "nome"
    );
    if (lastStickyIdx === -1) return stickyMap;

    let left = ACTIONS_COLUMN_WIDTH_PX;
    for (const { field } of visibleColumnSpecs) {
      const idx = MEETING_TABLE_COLUMN_SPECS.findIndex(
        (s) => s.field === field
      );
      if (idx === -1 || idx > lastStickyIdx) continue;
      stickyMap[field] = left;
      left += getColumnMinWidth(field);
    }

    return stickyMap;
  }, [visibleColumnSpecs, getColumnMinWidth]);

  const columnValueFilters = filters.columnFilters;

  const hasActiveColumnFilters = useMemo(
    () =>
      Object.entries(columnValueFilters).some(([, selected]) => {
        const values = selected ?? [];
        return (
          values.length > 0 &&
          !(values.length === 1 && String(values[0] ?? "").trim() === "")
        );
      }),
    [columnValueFilters]
  );

  const clearAllColumnFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      columnFilters: {},
    }));
  }, []);

  const handleApplyColumnFilter = useCallback(
    (field: string, values: string[]) => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        columnFilters: {
          ...prev.columnFilters,
          [field]: values.length === 0 ? [] : values,
        },
      }));
    },
    []
  );

  const handleClearColumnFilter = useCallback((field: string) => {
    setFilters((prev) => {
      const nextFilters = { ...prev.columnFilters };
      delete nextFilters[field];
      return {
        ...prev,
        page: 1,
        columnFilters: nextFilters,
      };
    });
  }, []);

  const handlePageChange = useCallback((pageNumber: number) => {
    setFilters((prev) => ({ ...prev, page: pageNumber }));
  }, []);

  const handleChangingRegistersPerPage = useCallback(
    (registersPerPage: number) => {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        take: registersPerPage,
      }));
    },
    []
  );

  const handleLoadOlderRecords = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      includeOlder: true,
    }));
  }, []);

  const handleShowRecentOnly = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      includeOlder: false,
    }));
  }, []);

  const columnFiltersCacheKey = useMemo(
    () =>
      JSON.stringify({
        includeOlder: filters.includeOlder,
        columnFilters: filters.columnFilters,
      }),
    [filters.includeOlder, filters.columnFilters]
  );

  const patchInteractionField = useCallback(
    async (
      interactionId: number,
      body: Record<string, unknown>,
      field: "status" | "duracao" | "paymentDay" | "feeCredito" | "paymentDeadline"
    ) => {
      const key = `${interactionId}-${field}`;
      setPatchingFieldKey(key);
      try {
        await api.patch(`/interactions/${interactionId}`, body);
        toast.success("Campo atualizado.", TOAST_SUCCESS_DEFAULT_OPTIONS);
        await invalidateInteractions();
      } catch (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
      } finally {
        setPatchingFieldKey(null);
      }
    },
    [invalidateInteractions]
  );

  const interactionListEditCtx = useMemo(
    () => ({
      onPatch: patchInteractionField,
      patchingFieldKey,
    }),
    [patchInteractionField, patchingFieldKey]
  );

  const fetchColumnValues = useCallback(
    async (field: string, page: number, search: string) => {
      return fetchInteractionColumnValues(field, filters, page, search);
    },
    [filters]
  );

  const handleConfigureColumns = (newColumns: Column[]) => {
    const vis: Record<string, boolean> = {};
    newColumns.forEach((c) => {
      vis[c.field] = c.isVisible !== false;
    });
    setColumnsVisibility(vis);
    localStorage.setItem(
      INTERACTIONS_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(vis)
    );
  };

  const handleDeleteRow = useCallback(
    async (doc: InteractionProps) => {
      if (!isAdmin) return;
      const ok = window.confirm(
        `Excluir a reunião #${doc.id}? Esta ação não pode ser desfeita.`
      );
      if (!ok) return;
      setDeletingId(doc.id);
      try {
        await api.delete(`/interactions/${doc.id}`);
        toast.success(
          "Reunião excluída com sucesso.",
          TOAST_SUCCESS_DEFAULT_OPTIONS
        );
        await invalidateInteractions();
      } catch (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
      } finally {
        setDeletingId(null);
      }
    },
    [isAdmin, invalidateInteractions]
  );

  const tableColumns = useMemo<Column[]>(
    () => [
      {
        field: "actions",
        headerName: "Ações",
        isSticky: true,
        stickyLeftInPx: 0,
        minWidthInPx: ACTIONS_COLUMN_WIDTH_PX,
        maxWidthInPx: ACTIONS_COLUMN_WIDTH_PX,
        renderCell: (row: { __doc: InteractionProps }) => {
          const doc = row.__doc;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FaRegEdit
                size={10}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDoc(doc);
                  setUploadModalOpen(true);
                  setEdit(true);
                }}
              />
              {isAdmin ? (
                <button
                  type="button"
                  title="Excluir reunião"
                  disabled={deletingId === doc.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDeleteRow(doc);
                  }}
                  style={{
                    cursor: deletingId === doc.id ? "wait" : "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "#b54b4b",
                    opacity: deletingId === doc.id ? 0.5 : 1,
                    verticalAlign: "middle",
                  }}
                >
                  <FaTrashAlt size={10} />
                </button>
              ) : null}
            </div>
          );
        },
      },
      ...visibleColumnSpecs.map(({ field, headerName }) => ({
        field,
        headerName,
        minWidthInPx: getColumnMinWidth(field),
        fitContent: columnFitsContent(field),
        maxWidthInPx: field === "nome" ? 220 : undefined,
        isSticky: stickyLeftByField[field] !== undefined,
        stickyLeftInPx: stickyLeftByField[field],
        hasMenuFilter: true,
        renderMenuFilter: ({
          isMenuOpen,
          dataTrigger,
          onCloseMenu,
        }: {
          isMenuOpen: boolean;
          dataTrigger: string;
          onCloseMenu: () => void;
        }) => (
          <ColumnFilterValuesMenu
            isOpen={isMenuOpen}
            dataTrigger={dataTrigger}
            cacheKey={`@interactions/${field}-${columnFiltersCacheKey}`}
            selectedValues={columnValueFilters[field] ?? []}
            onClose={onCloseMenu}
            onApplyFilter={(values) => handleApplyColumnFilter(field, values)}
            onClearFilter={() => handleClearColumnFilter(field)}
            onFetchColumnValues={(page, search) =>
              fetchColumnValues(field, page, search)
            }
            searchInputMask={
              field === "paymentDeadline" ? "99/9999" : undefined
            }
            searchPlaceholder={
              field === "paymentDeadline" ? "MM/AAAA" : "Buscar"
            }
            showSelectAll={field === "paymentDeadline"}
            getFilterLabel={
              field === "paymentDeadline"
                ? (value) =>
                    /^\d{2}\/\d{4}$/.test(value)
                      ? `${value} (mês inteiro)`
                      : value
                : undefined
            }
          />
        ),
        renderCell: (row: { __doc: InteractionProps }) => (
          <>{renderMeetingDataCell(field, row.__doc, interactionListEditCtx)}</>
        ),
      })),
    ],
    [
      visibleColumnSpecs,
      isAdmin,
      deletingId,
      handleDeleteRow,
      getColumnMinWidth,
      columnFitsContent,
      stickyLeftByField,
      columnValueFilters,
      columnFiltersCacheKey,
      fetchColumnValues,
      handleApplyColumnFilter,
      handleClearColumnFilter,
      interactionListEditCtx,
    ]
  );

  const tableRows = useMemo(
    () =>
      interactions.map((doc) => {
        const row: Record<string, unknown> = { actions: "" };
        visibleColumnSpecs.forEach(({ field }) => {
          row[field] = "";
        });
        row.id = doc.id;
        row.__doc = doc;
        return row;
      }),
    [interactions, visibleColumnSpecs]
  );

  const interactionCanceledRowClassName = useCallback(
    (row: Record<string, unknown>) => {
      const doc = row.__doc as InteractionProps | undefined;
      if (!doc) return undefined;
      if (doc.statusCode === 3 || doc.statusCode === 8) {
        return "bg-[#c5ced8]";
      }
      const label = String(doc.status ?? "").trim();
      if (label === "Canceled Client" || label === "Canceled Expert") {
        return "bg-[#c5ced8]";
      }
      return undefined;
    },
    []
  );

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const exportData = await fetchInteractionsForExport(filters);
      if (!exportData.length) {
        toast.info(
          "Nenhum registro para exportar com os filtros atuais da tabela.",
          {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
          }
        );
        return;
      }
      downloadInteractionsExcel(exportData, "interacoes");
      toast.success("Excel gerado com sucesso.", TOAST_SUCCESS_DEFAULT_OPTIONS);
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS);
    } finally {
      setIsExporting(false);
    }
  }, [filters, isAdmin]);

  return (
    <>
      <Modal open={uploadModalOpen} onClose={handleCloseModal}>
        <Box sx={newEntry}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignContent: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <button
              id="close-button"
              style={{
                display: "flex",
                flexDirection: "row",
                alignContent: "flex-end",
                justifyContent: "flex-end",
                cursor: "pointer",
                background: "none",
                border: "none",
              }}
              type="button"
              onClick={handleCloseModal}
            >
              <IoClose color="#b3b3b3" size={20} />
            </button>
          </div>
          <NewMeeting
            initialData={selectedDoc}
            edit={edit}
            onSaved={handleInteractionSaved}
          />
        </Box>
      </Modal>

      <Wrapper>
        <div
          className="add-doc"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            justifyContent: "flex-end",
            alignItems: "flex-start",
          }}
        >
          <div style={{ position: "relative" }}>
            <Button
              variant="outline"
              type="button"
              className={clsx({
                "bg-[#C9D8E1]": isTableColumnsConfigMenuOpen,
              })}
              onClick={() => setIsTableColumnsConfigMenuOpen(true)}
            >
              <SettingsSvg />
              <span className="text-sm whitespace-nowrap">
                Configurar tabela
              </span>
            </Button>
            <ConfigureTableColumnsMenu
              columns={columnsForConfigureMenu}
              isOpen={isTableColumnsConfigMenuOpen}
              onClose={() => setIsTableColumnsConfigMenuOpen(false)}
              onConfigureColumns={handleConfigureColumns}
            />
          </div>
          {hasActiveColumnFilters ? (
            <button
              type="button"
              className="text-xs underline text-primary-light self-center"
              onClick={clearAllColumnFilters}
            >
              Limpar todos os filtros
            </button>
          ) : null}
          {isAdmin ? (
            <div style={{ position: "relative" }}>
              <Button
                variant="outline"
                type="button"
                onClick={() => void handleExportExcel()}
                disabled={isExporting}
              >
                <span className="text-sm whitespace-nowrap">
                  {isExporting ? "Exportando..." : "Exportar Excel"}
                </span>
              </Button>
            </div>
          ) : null}
          <CustomButton
            id="upload-doc"
            type="button"
            text="Nova reunião"
            onClick={() => setUploadModalOpen(true)}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
            fontSize: 13,
            color: "#4a5568",
          }}
        >
          {filters.includeOlder ? (
            <>
              <span>Incluindo registros anteriores a 12 meses.</span>
              <button
                type="button"
                className="text-xs underline text-primary-light"
                onClick={handleShowRecentOnly}
              >
                Exibir apenas últimos 12 meses
              </button>
            </>
          ) : (
            <>
              <span>Exibindo reuniões dos últimos 12 meses.</span>
              {hasOlderRecords ? (
                <button
                  type="button"
                  className="text-xs underline text-primary-light"
                  onClick={handleLoadOlderRecords}
                >
                  Carregar registros anteriores
                </button>
              ) : null}
            </>
          )}
        </div>

        <DataTable
          columns={tableColumns}
          rows={tableRows}
          isLoading={isLoading}
          stripedRows
          rowClassName={interactionCanceledRowClassName}
          compactListingFont
          noContentMessage="Sem registro de reunião(ões)"
          currentPage={filters.page}
          registersPerPage={filters.take}
          totalCountOfRegisters={data?.meta?.itemCount}
          onPageChange={handlePageChange}
          onChangingRegistersPerPage={handleChangingRegistersPerPage}
        />
      </Wrapper>
    </>
  );
};

export default Interactions;
